import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Box, Text, useDisclosure, Flex, Button, Icon } from '@chakra-ui/react';
import { IReportTile } from '../../types';
import { ReportMetricCard } from '../ReportMetricCard';
import { useQuery } from '@tanstack/react-query';
import { API_SERVICE } from '../../api/api';
import { transformData } from '../../transformers/transformerMap';
import ReportCharts from '../ReportCharts/ReportCharts';
import { ReportTable } from '../ReportTable';
import { ReportTileSkeleton } from './ReportTileSkeleton';
import { useSelector, useDispatch } from 'react-redux';
import { getCustomReportData } from '@/store/custom-report';
import { getStaticReportData } from '@/store/static-report';
import { customReportActions } from '@/store/custom-report/customReportReducer';
import {
  USERS_KEY_BY_REPORT_TYPE,
  CUSTOM_REPORT_FILTER_KEYS,
  getUnsupportedFilterLabels,
} from './constant';
import { ReportTileActions } from './ReportTileActions';
import { FullScreenModal } from '@/componentsV1/FullScreenModal';
import { DrilldownModal } from '@/pages/reports/ats-reports/drilldown/DrilldownModal';
import { useDrilldownState } from '@/pages/reports/ats-reports/drilldown/useDrilldownState';
import { useFlowHealthDrilldownConfig } from '@/pages/reports/non-ats-reports/flow-health/candidate-touchpoint/component/table/useFlowHealthDrilldownConfig';
import { useDrilldownGroup } from '@/pages/reports/ats-reports/drilldown/useDrilldownGroup';
import { customReportDrilldownConfig } from '@/pages/reports/ats-reports/drilldown/constants';
import { useKFlags } from '@/hooks/use-k-flags';
import { DrilldownActivityPopup } from '@/pages/reports/non-ats-reports/flow-health/drilldownActivity';
import { useDeepCompareMemo } from 'use-deep-compare';
import SvgReload from '@/assets/icons/ai/Reload';
import { KText } from '@/ui-library/components/text';
import { ReportNpsScore } from '../ReportNpsScore';
import { fetchAllOutreachesAll } from '@/store/outreaches/actions';
import { fetchOutreachUsers } from '@/store/users/actions';

const MAX_MANUAL_RETRIES = 2;

interface ReportTileContainerProps {
  tile: IReportTile;
  isReadOnly?: boolean;
  isPrintView?: boolean;
  report?: Record<string, unknown>;
  handleTileAction?: (
    _tileId: string,
    _action: { ref: string; action: unknown }
  ) => void;
  layout?: { w: number; h: number };
  isBlockHovering?: boolean;
  isStaticReport?: boolean;
  viewMode?: boolean;
  isEditMode?: boolean;
  onRemoveBlock?: (_blockId: string) => void;
  onTileMetaChange?: (
    _tileId: string,
    _metadata: Record<string, unknown>
  ) => void;
  // Callback to receive local table filters from ReportTable
  onLocalTableFiltersChange?: (_filters: {
    metrics?: Record<string, unknown> | null;
    group_by_1?: Record<string, unknown> | null;
    group_by_2?: Record<string, unknown> | null;
  }) => void;
  getCurrentMergedData?: () => unknown;
}

export const ReportTileContainer: React.FC<ReportTileContainerProps> = ({
  tile,
  isReadOnly,
  isPrintView,
  handleTileAction: _handleTileAction,
  report,
  layout,
  isBlockHovering = false,
  isStaticReport = false,
  viewMode = false,
  isEditMode = false,
  onRemoveBlock,
  onTileMetaChange,
  onLocalTableFiltersChange: _onLocalTableFiltersChange,
  getCurrentMergedData,
}) => {
  const { id, subject_type, metadata, query } = tile || {};
  const { componentType, apiParam, defaultFilters, tileKey, uniqueTrackingId } =
    (metadata ?? {}) as {
      componentType?: string;
      apiParam?: string;
      defaultFilters?: Record<string, unknown>;
      tileKey?: string;
      uniqueTrackingId?: string;
    };
  const fullscreenDisclosure = useDisclosure();
  const tileActionMenuDisclosure = useDisclosure();
  const { feDrilldown, feReportsV3 } = useKFlags();
  const isFlow = ['flow_user_activity', 'flow_health']?.includes(subject_type);
  const dispatch = useDispatch();

  // Determine the appropriate identifier for Redux state management
  // For static reports or non-feReportsV3: use tileKey (existing behavior)
  // For feReportsV3 custom reports: use uniqueTrackingId or fallback to tile id for isolation
  const uniqueTileId = useMemo(() => {
    if (isStaticReport || !feReportsV3) {
      return tileKey;
    }
    // For custom reports with feReportsV3, use unique identifier to prevent cross-tile interference
    return uniqueTrackingId || id || tileKey;
  }, [isStaticReport, feReportsV3, tileKey, uniqueTrackingId, id]);

  // Local state to track table filters - only for feReportsV3
  const [localTableFilters, setLocalTableFilters] = useState({
    metrics: null,
    group_by_1: null,
    group_by_2: null,
  });

  // Track manual retry attempts
  const [manualRetryCount, setManualRetryCount] = useState(0);

  // Get current title from merged data (including ref changes)
  const getCurrentTitle = useCallback(() => {
    if (!getCurrentMergedData) return metadata?.title;

    const mergedData = getCurrentMergedData() as
      | {
          layoutData?: {
            tiles?: Array<{ id?: unknown; metadata?: { title?: string } }>;
          };
        }
      | undefined;
    const currentTile = mergedData?.layoutData?.tiles?.find(
      (t) => String(t?.id) === String(id)
    );
    return currentTile?.metadata?.title || metadata?.title;
  }, [getCurrentMergedData, metadata?.title, id]);

  useEffect(() => {
    if (feReportsV3 && componentType === 'table' && metadata?.filters) {
      const initialFilters = {
        metrics: metadata.filters.metrics ?? null,
        group_by_1: metadata.filters.group_by_1 ?? null,
        group_by_2: metadata.filters.group_by_2 ?? null,
      };
      setLocalTableFilters(initialFilters);
    }
  }, [componentType, feReportsV3, metadata.filters]);

  // Callback to receive table filter changes from ReportTable
  const handleTableFilterChange = useCallback(
    (key, option) => {
      const newFilters = {
        ...localTableFilters,
        [key]: option,
      };

      setLocalTableFilters(newFilters);

      // Also save to metadata for persistence
      onTileMetaChange?.(id, {
        filters: {
          [key]: option,
        },
      });
    },
    [localTableFilters, onTileMetaChange, id]
  );

  // Reinitialize filters when entering edit mode or metadata changes - only for feReportsV3
  useEffect(() => {
    if (
      feReportsV3 &&
      componentType === 'table' &&
      metadata?.filters &&
      isEditMode
    ) {
      const newFilters = {
        metrics: metadata.filters.metrics ?? null,
        group_by_1: metadata.filters.group_by_1 ?? null,
        group_by_2: metadata.filters.group_by_2 ?? null,
      };

      setLocalTableFilters(newFilters);
    }
  }, [feReportsV3, isEditMode, metadata?.filters, componentType, uniqueTileId]);

  const url = isFlow
    ? `/reports/${apiParam}` // Remove subject_type when it's 'flow'
    : //BE needs to unify this, in payload its eeoc and in api its eeo
      `/reports/${subject_type === 'eeoc' ? 'eeo' : subject_type}/${apiParam}`;

  const tableId = componentType === 'table' ? uniqueTileId : null;

  const { filterParams, tableGroups } = useSelector((state) =>
    isStaticReport
      ? getStaticReportData(state, tableId, componentType === 'card')
      : getCustomReportData(state, tableId, componentType === 'card')
  );

  const { metrics } = tableGroups[uniqueTileId] || {};

  // Initialize Redux state for new unique IDs (feReportsV3 only)
  useEffect(() => {
    if (
      feReportsV3 &&
      !isStaticReport &&
      componentType === 'table' &&
      uniqueTileId !== tileKey &&
      !tableGroups[uniqueTileId]
    ) {
      // Initialize this unique ID with the same defaults as the base tileKey
      const baseDefaults = tableGroups[tileKey] || {
        group_by_1: null,
        group_by_2: null,
      };
      dispatch(
        customReportActions.applyTableGroup({
          tableId: uniqueTileId,
          groupKey: 'group_by_1',
          value: baseDefaults.group_by_1,
        })
      );
      if (baseDefaults.group_by_2 !== undefined) {
        dispatch(
          customReportActions.applyTableGroup({
            tableId: uniqueTileId,
            groupKey: 'group_by_2',
            value: baseDefaults.group_by_2,
          })
        );
      }
      if (baseDefaults.metrics !== undefined) {
        dispatch(
          customReportActions.applyTableGroup({
            tableId: uniqueTileId,
            groupKey: 'metrics',
            value: baseDefaults.metrics,
          })
        );
      }
    }
    if (isFlow) {
      dispatch(fetchAllOutreachesAll({}));
      dispatch(fetchOutreachUsers(undefined));
    }
  }, [
    feReportsV3,
    isStaticReport,
    componentType,
    uniqueTileId,
    tileKey,
    tableGroups,
    dispatch,
    isFlow,
  ]);

  // Labels for applied filters that this report block does not support (shown as "N/A: ..." under heading)
  const unsupportedFilterLabels = useMemo(
    () =>
      getUnsupportedFilterLabels(
        subject_type,
        Object.keys(filterParams?.filter_by || {})
      ),
    [subject_type, filterParams?.filter_by]
  );

  const {
    reduxStore,
    apiFetch,
    drilldownTableConfig,
    metric,
    columnAccessor,
    applySearch,
    resetDrill,
  } = customReportDrilldownConfig;

  const drilldownData = useSelector(
    (state: Record<string, unknown>) =>
      (state[reduxStore?.[tileKey] as string] as Record<string, unknown>)
        ?.drilldownData || []
  );
  const isDrillLoading = useSelector(
    (state: Record<string, unknown>) =>
      (state[reduxStore?.[tileKey] as string] as Record<string, unknown>)
        ?.drilldownIsLoading
  );
  const currentDrillPage = useSelector(
    (state: Record<string, unknown>) =>
      (state[reduxStore?.[tileKey] as string] as Record<string, unknown>)
        ?.currentDrillPage ?? 1
  );

  const totalCount = useSelector(
    (state: Record<string, unknown>) =>
      (state[reduxStore?.[tileKey] as string] as Record<string, unknown>)
        ?.totalCount
  );
  const searchTerm = useSelector(
    (state: Record<string, unknown>) =>
      (state[reduxStore?.[tileKey] as string] as Record<string, unknown>)
        ?.searchTerm
  );

  const initialLoading = useSelector(
    (state: Record<string, unknown>) =>
      (state[reduxStore?.[tileKey] as string] as Record<string, unknown>)
        ?.initialLoading
  );

  const modalActions = useDisclosure();
  const [selected, setSelected] = useState(null);
  const [currentSortState, setCurrentSortState] = useState(null);

  const memoizedGroup = useMemo(
    () => ({
      group_by_1: query?.group_by_1,
      group_by_2: query?.group_by_2,
    }),
    [query?.group_by_1, query?.group_by_2]
  );

  const { drilldownState, setDrilldownState } = useDrilldownState({
    group: memoizedGroup,
    metricMapping: metric[subject_type],
  });

  const onDrillDownOpen = useCallback(
    (data) => {
      if (isFlow) {
        const id = metric[tileKey][data?.metric || ''];
        const rowData = {
          group_by: data?.flowdt,
          [id]: data.y,
          dt: data.flowdt,
          type: 'chart',
          g1: id,
          yAxis: data?.yAxis,
        };
        setSelected({
          id,
          row: {
            original: rowData,
            values: rowData,
          },
        });
      } else {
        setDrilldownState(data);
      }
      modalActions.onOpen();
    },
    [isFlow, metric, tileKey, setDrilldownState, modalActions]
  );

  const drilldownConfig = useMemo(
    () => ({
      storeSlice: reduxStore[subject_type],
      fetchDrilldownAction: apiFetch[subject_type],
      searchAction: applySearch[subject_type],
      resetDrill: resetDrill[subject_type],
      useColumnConfig: drilldownTableConfig[subject_type],
      metricMapping: metric[subject_type],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subject_type]
  );
  const group = filterParams || query;
  const groupinFilter = feReportsV3
    ? localTableFilters
    : {
        group_by_1: { id: group?.group_by_1?.id || group?.group_by_1 },
        group_by_2: { id: group?.group_by_2?.id || group?.group_by_2 },
      };
  const { getDrilldownGroup } = useDrilldownGroup(
    drilldownState,
    groupinFilter,
    subject_type
  );
  const drilldownGroup = getDrilldownGroup();

  // Dynamically filter `currentFilter.filter_by` based on `subject_type`
  const currentFilter = useMemo(() => {
    const allowedKeys = CUSTOM_REPORT_FILTER_KEYS[subject_type] || [];

    // For table components, use saved filters if Redux state is at defaults
    let baseFilters = query
      ? { ...defaultFilters, ...query }
      : { ...filterParams, ...defaultFilters };

    // If this is a table component, conditionally use local filters based on feReportsV3
    if (componentType === 'table') {
      if (feReportsV3 && localTableFilters) {
        // Use local table filters for reports-main (new state-based approach)
        const { group_by_1, group_by_2, metrics } = localTableFilters;
        baseFilters = {
          ...baseFilters,
          // eeoc don't support local filters - exclude them
          ...(subject_type !== 'eeoc'
            ? {
                group_by_1:
                  group_by_1?.id === null ? null : group_by_1?.id || group_by_1,
                group_by_2:
                  group_by_2?.id === null ? null : group_by_2?.id || group_by_2,
                metrics: metrics?.id === null ? null : metrics?.id || metrics,
              }
            : {}),
        };
      } else if (
        !feReportsV3 &&
        metadata.filters &&
        Object.keys(metadata.filters).length > 0
      ) {
        // Fallback to saved filters if no local filters and Redux state is at defaults
        const { group_by_1, group_by_2, metrics } = metadata.filters;

        // Only use saved filters if Redux state is at defaults (first load)
        const isFirstLoad =
          !tableGroups[uniqueTileId]?.group_by_1?.id &&
          !tableGroups[uniqueTileId]?.group_by_2?.id;

        if (isFirstLoad) {
          baseFilters = {
            ...baseFilters,
            group_by_1:
              group_by_1?.id === null ? null : group_by_1?.id || group_by_1,
            group_by_2:
              group_by_2?.id === null ? null : group_by_2?.id || group_by_2,
            metrics: metrics?.id === null ? null : metrics?.id || metrics,
          };
        }
      }
    }
    // Filter the `filter_by` section, preserving 'date' as an exception
    // live_job_pipeline does not use timeframe - exclude date from API payload
    // Replace users with report-type-specific key per APPLICATION_CREATOR_KEY_BY_REPORT_TYPE
    const shouldIncludeDate = subject_type !== 'live_job_pipeline';
    const usersApiKey = USERS_KEY_BY_REPORT_TYPE[subject_type];
    const filteredFilterBy = Object.keys(baseFilters.filter_by || {}).reduce(
      (acc, key) => {
        const value = baseFilters.filter_by[key];
        const isUsersFilter = key === 'users';
        const outputKey = isUsersFilter && usersApiKey ? usersApiKey : key;
        if (
          allowedKeys.includes(outputKey) ||
          (outputKey === 'date' && shouldIncludeDate)
        ) {
          acc[outputKey] = value;
        }
        return acc;
      },
      {}
    );

    const result = {
      ...baseFilters,
      filter_by: filteredFilterBy,
    };

    // live_job_pipeline does not use timeframe - exclude date_group and compare from API payload
    if (subject_type === 'live_job_pipeline') {
      delete result.date_group;
      delete result.compare;
    }

    return result;
  }, [
    subject_type,
    query,
    filterParams,
    defaultFilters,
    componentType,
    metadata.filters,
    tableGroups,
    uniqueTileId,
    localTableFilters,
    feReportsV3,
  ]);

  const queryKey = [url, currentFilter];

  const {
    data: apiData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKey,
    queryFn: () => API_SERVICE.getTileData(url, currentFilter, isFlow),
    staleTime: 180000, // 3 minutes
    enabled: Boolean(url),
    retry: 2,
  });

  // Reset retry count when query succeeds
  useEffect(() => {
    if (apiData && !error) {
      setManualRetryCount(0);
    }
  }, [apiData, error]);

  // Deeply memoized the transformed data to ensure stable reference
  const transformedData = useDeepCompareMemo(() => {
    if (!apiData) return null;
    return transformData({
      metadata,
      apiData,
      currentFilter,
      tile,
      subject_type,
    });
  }, [apiData, currentFilter, metadata]);

  // Handle manual retry with limit
  const handleManualRetry = useCallback(() => {
    if (manualRetryCount < MAX_MANUAL_RETRIES) {
      setManualRetryCount((prev) => prev + 1);
      refetch();
    }
  }, [manualRetryCount, refetch]);

  // Error component
  const renderErrorState = () => {
    const hasReachedMaxRetries = manualRetryCount >= MAX_MANUAL_RETRIES;

    return (
      <Flex h="100%" align="center" justify="center" flexWrap="wrap">
        <Flex
          gap="8px"
          alignItems="center"
          direction="column"
          textAlign="center"
        >
          <KText variant="heading_4">Unable to Load Report</KText>

          {hasReachedMaxRetries ? (
            <KText>
              Please refresh the page or contact support if the issue persists
            </KText>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={handleManualRetry}
              leftIcon={<Icon as={SvgReload} w="16px" h="16px" />}
              colorScheme="blue"
            >
              Retry
            </Button>
          )}
        </Flex>
      </Flex>
    );
  };

  // Determine if tile actions should be shown
  const shouldShowTileActions =
    (fullscreenDisclosure?.isOpen ||
      tileActionMenuDisclosure?.isOpen ||
      isBlockHovering) &&
    !error;

  const renderTileActions = () => {
    if (!shouldShowTileActions) return null;

    if (isPrintView) {
      return null;
    }

    let right = '0';
    if (feReportsV3) {
      right = isEditMode ? '-25px' : '-5px';
    } else {
      right = isStaticReport ? '-7px' : '-14px';
    }

    return (
      <Box position="absolute" right={right} top="0" zIndex={7}>
        <ReportTileActions
          tile={tile as unknown as Record<string, unknown>}
          uniqueTileId={uniqueTileId}
          report={{
            ...report,
            timeFrame: {
              date_group: filterParams?.date_group,
              from: filterParams?.filter_by?.date?.from,
              to: filterParams?.filter_by?.date?.to,
            },
            sort_by_direction: currentSortState?.desc ? 'desc' : 'asc',
            sort_by_column: currentSortState?.id || null,
            tile: tile,
            group: feReportsV3
              ? {
                  group_by_1: localTableFilters?.group_by_1?.id,
                  group_by_2: localTableFilters?.group_by_2?.id,
                }
              : {
                  group_by_1: group?.group_by_1?.id
                    ? group?.group_by_1?.id
                    : group?.group_by_1,
                  group_by_2: group?.group_by_2?.id
                    ? group?.group_by_2?.id
                    : group?.group_by_2,
                },
            metrics: feReportsV3
              ? localTableFilters?.metrics?.id ||
                localTableFilters?.metrics ||
                metrics?.id ||
                metrics
              : metrics?.id || metrics,
            filters: currentFilter?.filter_by,
          }}
          enableChartSwitcher={componentType === 'chart'}
          viewMode={viewMode}
          onFullScreen={fullscreenDisclosure.onOpen}
          transformedData={transformedData}
          tileActionMenuDisclosure={tileActionMenuDisclosure}
          isStaticReport={isStaticReport}
          customStageMapping={apiData?.misc?.stage_mapping}
          onRemoveBlock={onRemoveBlock}
          isEditMode={isEditMode}
          onTileMetaChange={onTileMetaChange}
        />
      </Box>
    );
  };

  const renderTileContent = () => {
    if (isLoading) {
      return <ReportTileSkeleton componentType={componentType} />;
    }

    if (error) {
      return renderErrorState();
    }

    switch (componentType) {
      case 'chart':
        return (
          <ReportCharts
            tileData={tile}
            key={`${tile.subject_type}-${tile.id}-${layout?.w}-${layout?.h}`}
            chartKey={tileKey}
            uniqueTileId={uniqueTileId}
            chartData={transformedData}
            isFullScreen={fullscreenDisclosure?.isOpen}
            onDrillDownOpen={onDrillDownOpen}
            isFlow={isFlow}
            isStaticReport={isStaticReport}
            isPrintView={isPrintView}
            onTileMetaChange={onTileMetaChange}
            isEditMode={isEditMode}
            unsupportedFilterLabels={unsupportedFilterLabels}
          />
        );
      case 'card':
        return (
          <ReportMetricCard
            tileData={tile}
            data={transformedData}
            isPrintView={isPrintView}
            isFullScreen={fullscreenDisclosure?.isOpen}
            onDrillDownOpen={onDrillDownOpen}
            isStaticReport={isStaticReport}
            onTileMetaChange={onTileMetaChange}
            currentTitle={getCurrentTitle()}
            unsupportedFilterLabels={unsupportedFilterLabels}
          />
        );
      case 'nps_score':
        return (
          <ReportNpsScore
            tileData={tile}
            data={transformedData}
            isPrintView={isPrintView}
            isFullScreen={fullscreenDisclosure?.isOpen}
            onDrillDownOpen={onDrillDownOpen}
            isStaticReport={isStaticReport}
            onTileMetaChange={onTileMetaChange}
            currentTitle={getCurrentTitle()}
            unsupportedFilterLabels={unsupportedFilterLabels}
          />
        );
      case 'table':
        return (
          <ReportTable
            data={transformedData}
            tile={tile}
            uniqueTileId={uniqueTileId}
            isFlow={isFlow}
            isReadOnly={isReadOnly}
            isPrintView={isPrintView}
            query={query ? query : undefined}
            isFullScreen={fullscreenDisclosure?.isOpen}
            onDrillDownOpen={onDrillDownOpen}
            customStageMapping={apiData?.misc?.stage_mapping}
            isStaticReport={isStaticReport}
            onSortChange={setCurrentSortState}
            onTileMetaChange={onTileMetaChange}
            isEditMode={isEditMode}
            currentTitle={getCurrentTitle()}
            unsupportedFilterLabels={unsupportedFilterLabels}
            // Conditional props based on feReportsV3 flag
            currentTableFilters={feReportsV3 ? localTableFilters : null}
            onTableFilterChange={
              feReportsV3 ? handleTableFilterChange : undefined
            }
          />
        );
      default:
        return <Text color="red.500">Unknown component type</Text>;
    }
  };

  const uniqueId = `${subject_type}-${id}`;

  const isCard = metadata?.componentType === 'card';

  return (
    <>
      <Box
        key={uniqueId}
        id={uniqueId}
        h="100%"
        className="tile-item-container"
        display="flex"
        flexDirection="column"
        position="relative"
      >
        {renderTileActions()}
        <Box flex="1" minH="0">
          {renderTileContent()}
        </Box>
      </Box>

      {fullscreenDisclosure.isOpen && (
        <FullScreenModal
          isOpen={fullscreenDisclosure.isOpen}
          onClose={fullscreenDisclosure.onClose}
          modalBodyProps={{
            alignItems: isCard ? 'center' : 'stretch',
            justifyContent: isCard ? 'center' : 'stretch',
          }}
          contentWrapperStyle={{
            width: isCard ? '35%' : '100%',
            height: isCard ? '35%' : '100%',
          }}
        >
          <Box h="100%" w="100%">
            {renderTileContent()}
          </Box>
        </FullScreenModal>
      )}

      {feDrilldown && modalActions.isOpen && !isFlow && (
        <DrilldownModal
          isOpen={modalActions.isOpen}
          onClose={modalActions.onClose}
          group={drilldownGroup}
          timeFrame={{
            date_group: filterParams?.date_group,
            from: filterParams?.filter_by?.date?.from,
            to: filterParams?.filter_by?.date?.to,
          }}
          columnAccessor={columnAccessor[subject_type]}
          filters={currentFilter?.filter_by}
          config={drilldownConfig}
          tile={tile}
          {...drilldownState.config}
        />
      )}

      {feDrilldown && modalActions.isOpen && isFlow && (
        <DrilldownActivityPopup
          isOpen={modalActions.isOpen}
          onClose={modalActions.onClose}
          filters={{
            date_group: filterParams?.date_group,
            from: filterParams?.filter_by?.date?.from,
            to: filterParams?.filter_by?.date?.to,
          }}
          selected={selected}
          filterParams={filterParams}
          fetchDrilldownAction={apiFetch[tileKey]}
          drilldownData={(drilldownData as unknown[]) ?? []}
          isLoading={Boolean(isDrillLoading)}
          currentDrillPage={Number(currentDrillPage) || 1}
          totalCount={Number(totalCount) || 0}
          applySearch={applySearch[tileKey] as (_params: unknown) => unknown}
          searchTerm={String(searchTerm ?? '')}
          useColumnConfig={useFlowHealthDrilldownConfig}
          resetDrill={resetDrill[tileKey] as () => void}
          initialLoading={Boolean(initialLoading)}
          tile={{
            subject_type: tile.metadata.apiParam,
            metadata: tile.metadata,
          }}
        />
      )}
    </>
  );
};
